import { UserProfile, UserRole } from "../services/userService";

// Angular Elements interfaces (matching the psx-components structure)
export interface MessagingKey {
  System: string;
  Id: string;
}

export interface PractitionerContactProperties {
  Credential?: string;
  FirstName?: string;
  IdentityId: string;
  LastName?: string;
  PracticeId?: string;
  PracticeName?: string;
  SpecialtyId?: string;
  SpecialtyName?: string;
}

export interface CareTeamMemberContactProperties {
  CallbackNumber?: string;
  DepartmentId?: string;
  DepartmentName?: string;
  ExtensionNumber?: string;
  FacilityGuid?: string;
  FacilityHsoId?: string;
  FacilityName?: string;
  FirstName?: string;
  IdentityId: string;
  LastName?: string;
  RoleId?: string;
  RoleName?: string;
  TitleId?: string;
  V5DepartmentId?: string;
  ConversationEndTimeReply?: number;
  IsPccta?: boolean;
}

export type PropertyType =
  | { key: "Practitioner"; value: PractitionerContactProperties }
  | { key: "CareTeamMember"; value: CareTeamMemberContactProperties };

export interface Persona {
  description: string;
  messagingKey: MessagingKey;
  displayName: string;
  scopes: string[];
  properties: PropertyType;
  isPccta?: boolean;
}

export interface UserConfiguration {
  identityId?: string;
  displayName?: string;
  availablePersonas?: Persona[];
  selectedPersona?: Persona;
  ShowFacilityNames?: boolean;
  permissions?: any; // Raw permissions API response data
}

/**
 * Helper function to create a practitioner persona
 */
function createPractitionerPersona(user: UserProfile, role: UserRole): Persona {
  return {
    description: `Practitioner: ${role.specialtyName || role.roleName} at ${role.practiceName || role.facilityName}`,
    messagingKey: role.messagingKey || { System: "Crm", Id: role.roleId },
    displayName: role.displayName || user.displayName,
    scopes: role.scopes || [user.uid],
    properties: {
      key: "Practitioner",
      value: {
        Credential: user.title || "",
        FirstName: user.firstName,
        IdentityId: user.uid,
        LastName: user.lastName,
        PracticeId: role.facilityGuid || role.organizationId || "",
        PracticeName: role.practiceName || role.facilityName,
        SpecialtyId: role.roleId,
        SpecialtyName: role.specialtyName || role.roleName,
      },
    },
  };
}

/**
 * Helper function to create a care team member persona
 */
function createCareTeamPersona(user: UserProfile, role: UserRole): Persona {
  return {
    description: `${role.roleName} at ${role.facilityName}${role.departmentName ? ` - ${role.departmentName}` : ""}`,
    messagingKey: role.messagingKey || { System: "AcuteCare", Id: role.roleId },
    displayName: user.displayName,
    scopes: role.scopes || [user.uid],
    properties: {
      key: "CareTeamMember",
      value: {
        CallbackNumber: "",
        DepartmentId: role.departmentName ? role.roleId : "",
        DepartmentName: role.departmentName || "",
        ExtensionNumber: "",
        FacilityGuid: role.facilityGuid || "",
        FacilityHsoId: role.facilityId || role.facilityGuid || "",
        FacilityName: role.facilityName,
        FirstName: user.firstName,
        IdentityId: user.uid,
        LastName: user.lastName,
        RoleId: role.roleId,
        RoleName: role.roleName,
        TitleId: role.roleId,
        V5DepartmentId: "",
        ConversationEndTimeReply: 0,
        IsPccta: false,
      },
    },
  };
}

/**
 * Converts shell UserRole and UserProfile to Angular Elements UserConfiguration
 */
export function createUserConfiguration(
  user: UserProfile,
  selectedRole: UserRole | null,
  allRoles: UserRole[]
): UserConfiguration {
  const availablePersonas: Persona[] = [];

  // Convert each role to a persona using rich byIdentityId data
  allRoles.forEach((role) => {
    if (role.isPractitioner) {
      availablePersonas.push(createPractitionerPersona(user, role));
    } else {
      availablePersonas.push(createCareTeamPersona(user, role));
    }
  });

  // Find the selected persona based on the selected role
  let selectedPersona: Persona | undefined;
  if (selectedRole) {
    selectedPersona = availablePersonas.find((persona) => {
      if (selectedRole.isPractitioner) {
        return persona.properties.key === "Practitioner";
      } else {
        return (
          persona.properties.key === "CareTeamMember" &&
          persona.properties.value.FacilityGuid === selectedRole.facilityGuid &&
          persona.properties.value.RoleId === selectedRole.roleId
        );
      }
    });
  }

  // Fallback to first practitioner if no specific selection
  if (!selectedPersona) {
    selectedPersona =
      availablePersonas.find((p) => p.properties.key === "Practitioner") || availablePersonas[0] || undefined;
  }

  // Add identity ID to scopes for each persona
  availablePersonas.forEach((persona) => {
    if (!persona.scopes.includes(user.uid)) {
      persona.scopes.push(user.uid);
    }
  });

  const userConfiguration: UserConfiguration = {
    identityId: user.uid,
    displayName: user.displayName,
    availablePersonas: availablePersonas,
    ...(selectedPersona && { selectedPersona }),
    ShowFacilityNames: true,
    permissions: user.permissions, // Include raw permissions API response data
  };

  return userConfiguration;
}
